import React, { useEffect, useState, useContext } from "react";
import { Button, Card, CardBody, Col, Row, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { OfferService } from "./service";
import { JobService } from "../job/service";
import { UserService } from "../user/service";
import { DeclineReasonService } from "../declineReason/service"; // Import DeclineReasonService
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import Select from "react-select";
import { EnumsContext } from "../../context/EnumsContext";
import { useTranslation } from "react-i18next";
import FormErrorSummary from "../../shared/components/FormErrorSummary";

const schema = yup.object().shape({
  job: yup.number().default(null).required('formErrors.jobRequired'), // Allow null but still required
  user: yup.number().default(null).required('formErrors.userRequired'), // Allow null but still required
  status: yup.string().required('formErrors.statusRequired'),
  note: yup.string(), // Optional field
  declineReason: yup.number().nullable(), // Optional field for decline reason
});

interface OfferState {
  job: number;
  user: number;
  status: string;
  note?: string;
  declineReason?: number | null; // Optional field for decline reason
}

const OfferForm: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [offerData, setOfferData] = useState<OfferState | null>(null);
  const [jobList, setJobList] = useState<any[]>([]);
  const [userList, setUserList] = useState<any[]>([]);
  const [declineReasonList, setDeclineReasonList] = useState<any[]>([]); // State for decline reasons
  const { enums, loading } = useContext(EnumsContext);
  const navigate = useNavigate();
  const { control, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<OfferState>({
    resolver: yupResolver(schema),
    defaultValues: {
      job: undefined, // Start as null
      user: undefined, // Start as null
      status: "",
      note: "",
      declineReason: null, // Default value for decline reason
    },
  });

  const status = watch("status"); // Watch the status field

  useEffect(() => {
    if (status !== "rejected") {
      setValue("declineReason", null); // Reset declineReason when status is not "rejected"
    }
  }, [status, setValue]);

  useEffect(() => {
    if (isEdit) {
      const fetchOffer = async () => {
        try {
          const response = await OfferService.getSingle(+id!);
          const offer = response;
          setOfferData({
            job: offer.job?.id || 0,
            user: offer.user?.id || 0,
            status: offer.status || "",
            note: offer.note || "",
            declineReason: offer.declineReason?.id || null, // Populate decline reason if available
          });
        } catch (error) {
          ErrorToast(error);
        }
      };

      fetchOffer();
    }
  }, [isEdit, id]);

  useEffect(() => {
    if (offerData) {
      reset(offerData);
    }
  }, [offerData, reset]);

  useEffect(() => {
    JobService.getAll({ perPage: 0, page: 1 }).then((res) => {
      setJobList(res.data.map((job: any) => ({ label: job.title, value: job.id })));
    }).catch((err) => ErrorToast(err));

    UserService.getAll({ perPage: 0, page: 1 }).then((res) => {
      setUserList(res.data.map((user: any) => ({ label: `${user.firstName} ${user.lastName}`, value: user.id })));
    }).catch((err) => ErrorToast(err));

    DeclineReasonService.getAll({ perPage: 0, page: 1 }).then((res) => {
      setDeclineReasonList(res.data.map((reason: any) => ({ label: reason.title, value: reason.id })));
    }).catch((err) => ErrorToast(err));
  }, []);

  const onSubmit = async (data: OfferState) => {
    try {
      isEdit
        ? await OfferService.update(data, +id!)
        : await OfferService.create(data);

      SuccessToast(t(`offer.${isEdit ? "successUpdate" : "successCreate"}`));
      navigate("/offers");
      reset();
    } catch (error) {
      ErrorToast(error);
    }
  };

  if (loading) {
    return <div>{t("offer.loading")}</div>;
  }

  const statusOptions = enums.offerStatus.map((status: string) => ({
    label: status.charAt(0).toUpperCase() + status.slice(1),
    value: status,
  }));

  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <Card>
            <CardBody>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <FormErrorSummary errors={errors} />
                <Row className="g-3">
                  <Col sm={12} xl={6}>
                    <div className="mb-0">
                      <label className="form-label">{t("offer.job")}</label>
                      <Controller
                        name="job"
                        control={control}
                        render={({ field }) => (
                          <Select
                            id="job"
                            classNamePrefix="react-select"
                            className={`react-select-container ${errors.job ? "is-invalid" : ""}`}
                            value={jobList.find((job) => job.value === field.value) || null}
                            onChange={(selectedOption) => field.onChange(selectedOption ? selectedOption.value : null)}
                            options={jobList}
                          />
                        )}
                      />
                      {errors.job && <div className="invalid-feedback">
                        {t(errors.job.message as string)}
                      </div>}
                    </div>
                  </Col>
                  <Col sm={12} xl={6}>
                    <div className="mb-0">
                      <label className="form-label">{t("offer.user")}</label>
                      <Controller
                        name="user"
                        control={control}
                        render={({ field }) => (
                          <Select
                            id="user"
                            classNamePrefix="react-select"
                            className={`react-select-container ${errors.user ? "is-invalid" : ""}`}
                            value={userList.find((user) => user.value === field.value) || null}
                            onChange={(selectedOption) => field.onChange(selectedOption ? selectedOption.value : null)}
                            options={userList}
                          />
                        )}
                      />
                      {errors.user && <div className="invalid-feedback">
                        {t(errors.user.message as string)}
                      </div>}
                    </div>
                  </Col>
                  <Col sm={12} xl={6}>
                    <div className="mb-0">
                      <label className="form-label">{t("offer.status")}</label>
                      <Controller
                        name="status"
                        control={control}
                        render={({ field }) => (
                          <Select
                            id="status"
                            classNamePrefix="react-select"
                            className={`react-select-container ${errors.status ? "is-invalid" : ""}`}
                            value={statusOptions.find((status: any) => status.value === field.value) || null}
                            onChange={(selectedOption) => field.onChange(selectedOption ? selectedOption.value : null)}
                            options={statusOptions}
                          />
                        )}
                      />
                      {errors.status && <div className="invalid-feedback">
                        {t(errors.status.message as string)}
                      </div>}
                    </div>
                  </Col>
                  {status === "rejected" && ( // Conditionally render declineReason field
                    <Col sm={12} xl={6}>
                      <div className="mb-0">
                        <label className="form-label">{t("offer.declineReason")}</label>
                        <Controller
                          name="declineReason"
                          control={control}
                          render={({ field }) => (
                            <Select
                              id="declineReason"
                              classNamePrefix="react-select"
                              value={declineReasonList.find((reason) => reason.value === field.value) || null}
                              onChange={(selectedOption) => field.onChange(selectedOption ? selectedOption.value : null)}
                              options={declineReasonList}
                              isClearable // Allow clearing the field
                            />
                          )}
                        />
                        {errors.declineReason && <div className="invalid-feedback">
                          {t(errors.declineReason.message as string)}
                        </div>}
                      </div>
                    </Col>
                  )}
                  <Col sm={12}>
                    <div className="mb-0">
                      <label className="form-label">{t("offer.note")}</label>
                      <Controller
                        name="note"
                        control={control}
                        render={({ field }) => (
                          <textarea
                            id="note"
                            className={`form-control ${errors.note ? "is-invalid" : ""}`}
                            placeholder={t("offer.enterNote")}
                            {...field}
                          />
                        )}
                      />
                      {errors.note && <div className="invalid-feedback">
                        {t(errors.note.message as string)}
                      </div>}
                    </div>
                  </Col>
                  <Col sm={12}>
                    <Button type="submit" variant="primary">
                      {t(`offer.${isEdit ? "update" : "create"}`)}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default OfferForm;